import { useState } from "react";
import { Link, useParams } from "wouter";
import {
  useGetEcoleEleveDetail,
  useEleveMotifsFacultatifs,
  useSetEleveMotifFacultatif,
} from "@/lib/backoffice-hooks";
import { useAnneeScope } from "@/lib/annee-scope";
import { formatDateShort } from "@/lib/format";
import EcoleShell from "@/components/layout/EcoleShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Download, PenSquare, Eye } from "lucide-react";
import { downloadRecuPdf } from "@/lib/downloadRecu";
import RecuPreviewDialog from "@/components/RecuPreviewDialog";
import type { EleveMotifFacultatif } from "@/lib/api";
import { friendlyError } from "@/lib/errors";

function fcfa(n: number) {
  return new Intl.NumberFormat("fr-FR").format(n) + " FCFA";
}
const STATUS_LABEL: Record<string, string> = {
  SUCCESS: "Encaissé", PROCESSING: "En cours", PENDING: "En attente",
  FAILED: "Échoué", CANCELLED: "Annulé", REFUNDED: "Remboursé",
};

export default function EcoleEleveDetailPage() {
  const params = useParams<{ eleveId: string }>();
  const [apercu, setApercu] = useState<{ id: string; reference: string } | null>(null);
  const { data, isLoading, error } = useGetEcoleEleveDetail(params.eleveId);

  if (isLoading) {
    return (
      <EcoleShell>
        <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>
      </EcoleShell>
    );
  }
  if (error || !data) {
    return (
      <EcoleShell>
        <div className="text-center py-16 text-slate-600">
          <p>Élève introuvable, ou l'endpoint backend <code>/api/ecole/eleves/{params.eleveId}</code> n'est pas encore disponible.</p>
          <Link href="/ecole/eleves" className="text-navy underline text-sm">← Retour à la liste</Link>
        </div>
      </EcoleShell>
    );
  }

  const { eleve, soldes, paiements, totalDuXof, totalPayeXof, totalRestantXof } = data;
  const tauxPaye = totalDuXof > 0 ? Math.min(100, Math.round((totalPayeXof / totalDuXof) * 100)) : 0;

  return (
    <EcoleShell>
      <div className="mb-5">
        <Link href="/ecole/eleves" className="text-xs text-slate-500 hover:text-navy inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Retour aux élèves
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Fiche élève</div>
            <h1 className="text-2xl font-bold text-navy">{eleve.prenoms} <span className="uppercase">{eleve.nom}</span></h1>
            <div className="text-sm text-slate-600 mt-1 flex gap-3">
              <span className="font-mono text-xs" style={{ fontFamily: "JetBrains Mono, monospace" }}>{eleve.matriculeInterne}</span>
              <span>·</span>
              <span>{eleve.classe ?? "Classe non renseignée"}</span>
              <span>·</span>
              <span>{eleve.anneeScolaire}</span>
              {eleve.sexe && (<><span>·</span><span>{eleve.sexe}</span></>)}
              {eleve.matriculeSedecOfficiel && (<><span>·</span><span className="font-mono text-xs" style={{ fontFamily: "JetBrains Mono, monospace" }}>SEDEC {eleve.matriculeSedecOfficiel}</span></>)}
            </div>
          </div>
          <Link href={`/ecole/saisie?eleveId=${eleve.id}`}>
            <Button className="bg-gold hover:bg-gold/90 text-navy font-bold" data-testid="button-encaisser-eleve">
              <PenSquare className="w-4 h-4 mr-1.5" /> Encaisser un paiement
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KpiCard label="Total dû (motifs actifs)" value={fcfa(totalDuXof)} />
        <KpiCard label="Total encaissé" value={fcfa(totalPayeXof)} tone="success" />
        <KpiCard label="Reste à payer" value={fcfa(totalRestantXof)} tone={totalRestantXof > 0 ? "warning" : "success"} />
        <KpiCard label="Taux de couverture" value={`${tauxPaye}%`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <Card className="lg:col-span-3 border border-slate-200 shadow-none overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h2 className="text-sm font-semibold text-navy">Solde par motif</h2>
          </div>
          {soldes.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500">
              Aucun motif actif. <Link href="/ecole/motifs" className="text-navy underline">Configurer le catalogue</Link>
            </div>
          )}
          {soldes.length > 0 && (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2">Motif</th>
                  <th className="text-right">Dû</th>
                  <th className="text-right">Payé</th>
                  <th className="text-right pr-2">Restant</th>
                  <th className="text-right pr-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {soldes.map((s) => {
                  const ok = s.restantXof <= 0;
                  return (
                    <tr key={s.motifId} className="border-b border-slate-100" data-testid={`row-solde-${s.motifCode}`}>
                      <td className="px-4 py-2">
                        <div className="font-medium">{s.motifLibelle}</div>
                        <div className="text-xs text-slate-500 font-mono" style={{ fontFamily: "JetBrains Mono, monospace" }}>{s.motifCode}</div>
                      </td>
                      <td className="text-right font-mono text-slate-700" style={{ fontFamily: "JetBrains Mono, monospace" }}>{fcfa(s.duXof)}</td>
                      <td className="text-right font-mono text-emerald-700" style={{ fontFamily: "JetBrains Mono, monospace" }}>{fcfa(s.payeXof)}</td>
                      <td className={`text-right font-mono pr-2 font-semibold ${ok ? "text-emerald-700" : "text-amber-700"}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        {ok ? "✓ Soldé" : fcfa(s.restantXof)}
                      </td>
                      <td className="text-right pr-4">
                        {!ok && (
                          <Link href={`/ecole/saisie?eleveId=${eleve.id}&motifId=${s.motifId}`}>
                            <button className="text-xs px-2 py-1 border border-navy/30 text-navy rounded hover:bg-navy/5">Encaisser</button>
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </Card>

        <Card className="lg:col-span-2 border border-slate-200 shadow-none overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h2 className="text-sm font-semibold text-navy">Historique des paiements ({paiements.length})</h2>
          </div>
          {paiements.length === 0 && <div className="p-6 text-center text-sm text-slate-500">Aucun paiement enregistré.</div>}
          <div className="max-h-[600px] overflow-auto">
            {paiements.map((p) => (
              <div key={p.id} className="px-4 py-2.5 border-b border-slate-100 hover:bg-slate-50" data-testid={`row-paiement-${p.reference}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs text-navy" style={{ fontFamily: "JetBrains Mono, monospace" }}>{p.reference}</div>
                    <div className="text-xs text-slate-600 truncate">{p.motifLibelle}</div>
                    <div className="text-[11px] text-slate-400">
                      {formatDateShort(p.paidAt ?? p.createdAt)} · {String(p.methode).replace(/_/g, " ")}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm font-semibold" style={{ fontFamily: "JetBrains Mono, monospace" }}>{fcfa(p.montantXof)}</div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${p.status === "SUCCESS" ? "bg-emerald-100 text-emerald-700" : p.status === "FAILED" || p.status === "CANCELLED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                      {STATUS_LABEL[p.status] ?? p.status}
                    </span>
                  </div>
                  {p.status === "SUCCESS" && (
                    <div className="inline-flex items-center gap-0.5">
                      <button
                        onClick={() => setApercu({ id: p.id, reference: p.reference })}
                        title="Aperçu du reçu"
                        className="text-slate-500 hover:text-navy p-1"
                        data-testid={`button-apercu-${p.reference}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => downloadRecuPdf(p.id, p.reference)}
                        title="Télécharger le reçu PDF"
                        className="text-slate-500 hover:text-navy p-1"
                        data-testid={`button-pdf-${p.reference}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <MotifsFacultatifsCard
        eleveId={eleve.id}
        eleveNom={`${eleve.prenoms} ${eleve.nom}`}
      />

      <RecuPreviewDialog
        open={apercu !== null}
        onOpenChange={(o) => !o && setApercu(null)}
        source={{ kind: "auth", paiementId: apercu?.id ?? "" }}
        reference={apercu?.reference}
      />
    </EcoleShell>
  );
}

/**
 * Motifs FACULTATIFS (cantine, transport…) : l'économe les active élève par
 * élève, avec confirmation. Un motif payé est VERROUILLÉ tant que ses
 * paiements ne sont pas annulés.
 */
function MotifsFacultatifsCard({ eleveId, eleveNom }: { eleveId: string; eleveNom: string }) {
  const { isActiveAnnee } = useAnneeScope();
  const { data: facultatifs, isLoading } = useEleveMotifsFacultatifs(eleveId);
  const mutation = useSetEleveMotifFacultatif(eleveId);
  const [confirmation, setConfirmation] = useState<EleveMotifFacultatif | null>(null);

  const liste = facultatifs ?? [];
  // Rien à gérer : ni carte vide, ni bruit — la plupart des écoles n'ont que
  // des motifs obligatoires.
  if (!isLoading && liste.length === 0) return null;

  function confirmer() {
    /* v8 ignore start -- défensif : confirmer n'est appelable que depuis le dialog rendu quand confirmation est non nul */
    if (!confirmation) return;
    /* v8 ignore stop */
    const cible = confirmation;
    mutation.mutate(
      { motifId: cible.motifId, actif: !cible.active },
      {
        onSuccess: () => {
          toast.success(
            cible.active
              ? `« ${cible.libelle} » désactivé pour ${eleveNom}`
              : `« ${cible.libelle} » activé pour ${eleveNom}`,
          );
          setConfirmation(null);
        },
        onError: (err: unknown) => toast.error(friendlyError(err)),
      },
    );
  }

  return (
    <Card className="mt-5 border border-slate-200 shadow-none overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
        <h2 className="text-sm font-semibold text-navy">Motifs facultatifs</h2>
        <p className="text-xs text-slate-500">
          Cantine, transport… facturés uniquement si vous les activez pour cet élève.
        </p>
      </div>
      {isLoading && (
        <div className="p-6 text-center">
          <Loader2 className="w-5 h-5 animate-spin inline text-navy" />
        </div>
      )}
      {!isLoading && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2">Motif</th>
                <th className="text-left">État</th>
                <th className="text-right">Tarif (sa classe)</th>
                <th className="text-right">Payé</th>
                <th className="text-right pr-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((f) => (
                <tr key={f.motifId} className="border-b border-slate-100" data-testid={`row-facultatif-${f.code}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{f.libelle}</div>
                    <div className="text-xs text-slate-500 font-mono" style={{ fontFamily: "JetBrains Mono, monospace" }}>{f.code}</div>
                  </td>
                  <td>
                    <span
                      className={`text-[11px] px-1.5 py-0.5 rounded ${f.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                      data-testid={`etat-facultatif-${f.code}`}
                    >
                      {f.active ? "Activé" : "Non activé"}
                    </span>
                  </td>
                  <td className="text-right font-mono" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    {f.duXof > 0 ? fcfa(f.duXof) : "·"}
                  </td>
                  <td className="text-right font-mono text-emerald-700" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    {f.payeXof > 0 ? fcfa(f.payeXof) : "·"}
                  </td>
                  <td className="text-right pr-4 py-1.5">
                    <button
                      onClick={() => setConfirmation(f)}
                      disabled={!isActiveAnnee || (f.active && f.verrouille)}
                      title={
                        f.active && f.verrouille
                          ? "Des paiements validés existent : annulez-les avant de désactiver"
                          : undefined
                      }
                      className={`text-xs px-2 py-1 border rounded inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed ${
                        f.active
                          ? "border-red-200 text-red-600 hover:bg-red-50"
                          : "border-navy/30 text-navy hover:bg-navy/5"
                      }`}
                      data-testid={`button-toggle-facultatif-${f.code}`}
                    >
                      {f.active ? "Désactiver" : "Activer"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={!!confirmation}
        onOpenChange={(o) => {
          /* v8 ignore start -- défensif : sans DialogTrigger, Radix n'appelle onOpenChange qu'avec o=false (fermeture) ; la branche o=true est inatteignable */
          if (!o) setConfirmation(null);
          /* v8 ignore stop */
        }}
      >
        <DialogContent className="max-w-sm" data-testid="dialog-confirm-facultatif">
          <DialogHeader>
            <DialogTitle>
              {confirmation?.active ? "Désactiver le motif" : "Activer le motif"}
            </DialogTitle>
            <DialogDescription>
              {confirmation?.active
                ? `Désactiver « ${confirmation?.libelle} » pour ${eleveNom} : l'élève ne sera plus facturé pour ce motif.`
                : `Activer « ${confirmation?.libelle} » pour ${eleveNom} : le motif entre dans son dû` +
                  (confirmation && confirmation.duXof > 0 ? ` (${fcfa(confirmation.duXof)}).` : " (tarif de sa classe).")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmation(null)} data-testid="button-cancel-facultatif">
              Annuler
            </Button>
            <Button
              onClick={confirmer}
              disabled={mutation.isPending}
              className={confirmation?.active ? "bg-red-600 text-white hover:bg-red-700" : "bg-navy text-white hover:bg-navy/90"}
              data-testid="button-confirm-facultatif"
            >
              {mutation.isPending ? "Enregistrement…" : confirmation?.active ? "Désactiver" : "Activer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function KpiCard({ label, value, tone }: { label: string; value: string; tone?: "success" | "warning" }) {
  const color = tone === "success" ? "text-emerald-700" : tone === "warning" ? "text-amber-700" : "text-navy";
  return (
    <Card className="p-4 border border-slate-200 shadow-none">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-bold mt-1 font-mono ${color}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>{value}</div>
    </Card>
  );
}
